import { ArrowBack, ArrowForward } from '@mui/icons-material';
import React, { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';

import CButton from '../../components/CButton';
import { updateStateAttribute } from '../../helpers/helper';

interface Props {
  viewportPadding: {
    paddingLeft: number;
    paddingRight: number;
  };
  children?: ReactNode;
  gap?: CSSProperties['gap'];
  interval?: number;
}

interface State {
  cardsInView: number[];
}

export default function CMagneticSnapCarousel(props: Props) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<State>({
    cardsInView: []
  });

  const childrenCount = React.Children.count(props.children);

  const scrollToCard = (target: HTMLDivElement) => {
    const scrollerEl = target.parentElement as HTMLDivElement;
    const targetRect = target.getBoundingClientRect();
    const scrollerRect = scrollerEl.getBoundingClientRect();

    const viewportLeft = scrollerRect.left + props.viewportPadding.paddingLeft;
    const viewportRight = scrollerRect.right - props.viewportPadding.paddingRight;

    if (targetRect.right > viewportRight) {
      const scrollDistance = targetRect.right - viewportRight;

      scrollerEl.scrollBy({
        left: scrollDistance,
        behavior: 'smooth'
      });

      return;
    }

    if (targetRect.left < viewportLeft) {
      const scrollDistance = targetRect.left - viewportLeft;

      scrollerEl.scrollBy({
        left: scrollDistance,
        behavior: 'smooth'
      });
    }
  };

  const scrollToIndex = (index: number) => {
    const target = scrollContainerRef.current?.children[index] as HTMLDivElement;

    if (target) {
      scrollToCard(target);
    }
  };

  const modulo = (n: number, div: number) => {
    return ((n % div) + div) % div;
  };

  const handlePrevClick = () => {
    const currentIndex = state.cardsInView[0];
    const prevIndex = modulo(currentIndex - 1, childrenCount + 1);

    scrollToIndex(prevIndex !== 0 ? prevIndex : childrenCount + 1);
  };

  const handleNextClick = () => {
    const currentIndex = [...state.cardsInView].pop() ?? 0;
    const nextIndex = modulo(currentIndex + 1, childrenCount + 1);

    scrollToIndex(nextIndex);
  };

  useEffect(() => {
    const computeCardsInView = () => {
      const marginOfError = 10;
      const viewPortLeft = viewportRef.current?.getBoundingClientRect().left ?? 0;
      const viewPortWidth = viewportRef.current?.getBoundingClientRect().width ?? 0;

      const viewport = [
        props.viewportPadding.paddingLeft - marginOfError + viewPortLeft,
        viewPortWidth - props.viewportPadding.paddingRight + marginOfError + viewPortLeft
      ];

      updateStateAttribute<State>(
        {
          cardsInView: Array.from(scrollContainerRef.current?.children ?? [])
            .slice(0, -1)
            .map((card, index) => {
              return card.getBoundingClientRect().left >= viewport[0] &&
                card.getBoundingClientRect().right <= viewport[1]
                ? index
                : undefined;
            })
            .filter((index): index is number => index !== undefined)
        },
        setState
      );
    };

    computeCardsInView();

    const scroller = scrollContainerRef.current;

    scroller?.addEventListener('scroll', computeCardsInView);
    window.addEventListener('resize', computeCardsInView);

    return () => {
      scroller?.removeEventListener('scroll', computeCardsInView);
      window.removeEventListener('resize', computeCardsInView);
    };
  }, [props.viewportPadding.paddingLeft, props.viewportPadding.paddingRight, props.children]);

  useEffect(() => {
    if (!props.interval) return;

    const autoPlay = setInterval(handleNextClick, props.interval * 1000);

    return () => {
      clearInterval(autoPlay);
    };
  }, [props.interval, state.cardsInView]);

  return (
    <div className={'flex flex-col gap-4'} ref={viewportRef}>
      <div
        ref={scrollContainerRef}
        className={'w-full flex overflow-x-scroll snap-x snap-mandatory py-2 -my-2'}
        style={{
          scrollbarWidth: 'none',
          gap: props.gap ?? '1rem'
        }}
      >
        <div
          style={{
            width: props.viewportPadding.paddingLeft - 15,
            minWidth: props.viewportPadding.paddingLeft - 15
          }}
        />

        {React.Children.map(props.children, (child, index) => (
          <div
            key={index}
            className={'relative snap-start'}
            style={{
              scrollMarginLeft: props.viewportPadding.paddingLeft
            }}
          >
            {child}
          </div>
        ))}

        <div
          style={{
            width: props.viewportPadding.paddingRight - 15,
            minWidth: props.viewportPadding.paddingRight - 15
          }}
        />
      </div>

      {state.cardsInView.length !== childrenCount && (
        <div
          style={{
            paddingLeft: props.viewportPadding.paddingLeft,
            paddingRight: props.viewportPadding.paddingRight
          }}
        >
          <div className={'flex gap-8 items-center flex-nowrap justify-end'}>
            <div className={'hidden sm:flex justify-center gap-4 text-sm flex-wrap'}>
              {Array(childrenCount)
                .fill(undefined)
                .map((_, index) => (
                  <button
                    key={index}
                    className={
                      'relative flex items-center justify-center cursor-pointer opacity-70 ' +
                      'appearance-none border-0 bg-transparent leading-none ' +
                      'pointer-fine:p-0 pointer-coarse:p-3'
                    }
                    onClick={() => {
                      scrollToIndex(index + 1);
                    }}
                  >
                    <span
                      className={
                        'block w-2.5 h-2.5 bg-slate-300 rounded-full overflow-hidden relative transition-[width]'
                      }
                      style={{ width: props.interval ? (state.cardsInView.includes(index + 1) ? 30 : 10) : 10 }}
                    >
                      <span
                        className={'absolute top-0 left-0 h-full bg-slate-700 transition-[width] ease-linear'}
                        style={{
                          width: props.interval ? (state.cardsInView.includes(index + 1) ? '100%' : 0) : '100%',
                          transitionDuration: state.cardsInView.includes(index + 1)
                            ? `${(props.interval ?? 0) * 1000 * 1.2}ms`
                            : '0s'
                        }}
                      />
                    </span>
                  </button>
                ))}
            </div>

            <div className={'flex gap-2'}>
              <CButton
                onClick={() => {
                  if (state.cardsInView[0] !== 1) {
                    handlePrevClick();
                  }
                }}
                style={{ borderWidth: '1px', justifyContent: 'center' }}
                disabled={state.cardsInView[0] === 1}
              >
                <ArrowBack />
              </CButton>

              <CButton
                onClick={() => {
                  if ([...state.cardsInView].pop() !== childrenCount) {
                    handleNextClick();
                  }
                }}
                style={{ borderWidth: '1px', justifyContent: 'center' }}
                disabled={[...state.cardsInView].pop() === childrenCount}
              >
                <ArrowForward />
              </CButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
